'use client'

import { ArrowRight, Check } from 'lucide-react'
import { useEffect, useState } from 'react'
import { ENABLE_SYSTEM_DESIGN_INTERVIEW } from '@/lib/config'
import {
  fetchProductPacks,
  getInterviewPracticeCurrency,
  getInterviewPracticeDisplayPrice,
  type ProductPack,
} from '@/lib/productPacks'
import { formatLandingPrice, showLandingGstNote } from '@/lib/formatLandingPrice'
import styles from '../newLanding.module.css'
import { LandingJourneyCta } from './LandingJourneyCta'
import { LandingRevealOnView } from './LandingRevealOnView'
import { useCountUp } from '../hooks/useCountUp'

function PlanFeatures({ lines }: { lines: string[] }) {
  return (
    <ul className={styles.singlePlansFeatureList}>
      {lines.map((text) => (
        <li key={text} className={styles.singlePlansFeatureRow}>
          <span className={styles.singlePlansCheckIcon} aria-hidden="true">
            <Check size={12} strokeWidth={3} />
          </span>
          <span>{text}</span>
        </li>
      ))}
    </ul>
  )
}

function PriceDisplay({ amount, currency }: { amount: number | undefined; currency: string }) {
  const animatedAmount = useCountUp(amount)
  if (amount == null) return null
  return (
    <div className={styles.singlePlansPriceRow}>
      <span className={styles.singlePlansAmount}>
        {formatLandingPrice(animatedAmount ?? amount, currency)}
      </span>
      {showLandingGstNote(currency) ? (
        <span className={styles.singlePlansGst}>+GST</span>
      ) : null}
    </div>
  )
}

export function NewUISinglePlans() {
  const [packs, setPacks] = useState<ProductPack[]>([])
  const [ready, setReady] = useState(false)

  useEffect(() => {
    let cancelled = false

    void fetchProductPacks()
      .then((data) => {
        if (!cancelled) setPacks(data)
      })
      .catch(() => {
        // Prices stay hidden when the catalog endpoint is unavailable.
      })
      .finally(() => {
        if (!cancelled) setReady(true)
      })

    return () => {
      cancelled = true
    }
  }, [])

  const currency = ready ? getInterviewPracticeCurrency(packs) : 'INR'
  const mockPrice = ready ? getInterviewPracticeDisplayPrice(packs, 'skill_assessment') : undefined
  const jdPrice = ready ? getInterviewPracticeDisplayPrice(packs, 'jd_prep') : undefined
  const systemDesignPrice = ready
    ? getInterviewPracticeDisplayPrice(packs, 'system_design')
    : undefined

  return (
    <section id="pricing" className={styles.singlePlansSection} aria-labelledby="single-plans-heading">
      <div className={styles.singlePlansInner}>
        <LandingRevealOnView className={styles.singlePlansHeader}>
          <span className={styles.heroBadge}>Single Plans</span>
          <h2 id="single-plans-heading" className={styles.singlePlansHeading}>
            Choose Your Interview Practice
          </h2>
          <p className={styles.singlePlansSubheading}>
            Real interview experience. Real feedback. Real growth.
          </p>
        </LandingRevealOnView>

        <div className={styles.singlePlansGrid}>
          <LandingRevealOnView as="article" className={styles.singlePlansCard} delayMs={0}>
            <div className={styles.singlePlansCardHead}>
              <p className={styles.singlePlansTierLabel}>Mock Interview</p>
            </div>
            <PriceDisplay amount={mockPrice} currency={currency} />
            <LandingJourneyCta journey="resume" ctaSurface="pricing" className={styles.singlePlansCta}>
              Start Practicing
              <ArrowRight size={16} className={styles.ctaArrowIcon} aria-hidden="true" />
            </LandingJourneyCta>
            <PlanFeatures
              lines={[
                'CV-based personalized questions',
                'Real interview-like experience',
                'Detailed performance feedback',
                'Improve confidence & communication',
                'Quick, on demand mock interview practice',
              ]}
            />
          </LandingRevealOnView>

          <LandingRevealOnView
            as="article"
            className={`${styles.singlePlansCard} ${styles.singlePlansCardFeatured}`}
            delayMs={120}
          >
            <div className={styles.singlePlansCardHead}>
              <span className={styles.comboBadgeRecommended}>Most Popular</span>
              <p className={styles.singlePlansTierLabel}>JD Interview</p>
            </div>
            <PriceDisplay amount={jdPrice} currency={currency} />
            <LandingJourneyCta journey="jd" ctaSurface="pricing" className={styles.singlePlansCtaFeatured}>
              Start Practicing
              <ArrowRight size={16} className={styles.ctaArrowIcon} aria-hidden="true" />
            </LandingJourneyCta>
            <PlanFeatures
              lines={[
                'Questions tailored to the job description',
                'Role-specific skill evaluation',
                'Real-world interview scenarios',
                'Actionable feedback for improvement',
                'Align preparation with company expectations',
              ]}
            />
          </LandingRevealOnView>

          <LandingRevealOnView as="article" className={styles.singlePlansCard} delayMs={240}>
            <div className={styles.singlePlansCardHead}>
              <p className={styles.singlePlansTierLabel}>System Design Interview</p>
            </div>
            <PriceDisplay amount={systemDesignPrice} currency={currency} />
            {ENABLE_SYSTEM_DESIGN_INTERVIEW ? (
              <LandingJourneyCta journey="system-design" ctaSurface="pricing" className={styles.singlePlansCta}>
                Start Practicing
                <ArrowRight size={16} className={styles.ctaArrowIcon} aria-hidden="true" />
              </LandingJourneyCta>
            ) : (
              <span className={styles.singlePlansComingSoonCta} aria-disabled="true">
                Coming Soon
              </span>
            )}
            <PlanFeatures
              lines={[
                'Solve real-world system design problems',
                'Practice scalability & architecture thinking',
                'Explain and defend your approach',
                'Structured feedback on design decisions',
                'Ideal for senior-level interviews',
              ]}
            />
          </LandingRevealOnView>
        </div>
      </div>
    </section>
  )
}
